import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { cx, isNotEmptyArray } from '@travel/utils';
import { Filter } from '@travel/icons/ui';
import { IconWithBadge } from '@travel/ui';
import { useApiClient } from '@travel/api-client';
import { useDialogHandler, useDeviceType } from '@travel/traveler-core/hooks';
import { SortKeysType } from '@travel/traveler-core/constants/provider';
import { seoKeys } from '@travel/traveler-core/utils/seo';

import { Translate } from 'core/translate';
import ProviderFilterTags from 'components/ProviderFilterTags';
import FilterAndSort from '../FilterAndSort';
import StyledButton from './StyledButton';

import { FILTER_GROUP_IDS } from 'constants/provider';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { getRequestBody } from 'pages/ProviderListPage/resolver';
import { SearchFormQuery } from 'ProviderList-Types';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';

import { fetchProviderListItems } from 'store/providerList/api';
import {
  getItemsTotal,
  getFilters,
  getSelectedObjectivesFeatureIdMapping,
} from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import styles from './filterContainer.module.scss';

export type PriceRangeType = number | null | undefined;

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  className?: string;
  filterButtonClassName?: string;
  /** String of recent path id */
  pathId: string;
  /** Object of recent search query */
  query: SearchFormQuery;
  /** Callback to be called when search button has been clicked */
  onSearch: () => void;
  /** Callback to be called when remove selected coupon */
  onRemoveCoupon?: () => void;
  setSortKey?: React.Dispatch<React.SetStateAction<SortKeysType>>;
};

const DEBOUNCE_TIME = 300;

export const toggleFilterOption = (currentOptions: Array<string>, targetOption: string) => {
  if (currentOptions.includes(targetOption)) {
    return currentOptions.filter(opt => opt !== targetOption);
  }

  return [...currentOptions, targetOption];
};

function FilterContainer(props: Props) {
  const apiClient = useApiClient();
  const { query, onPage, onRemoveCoupon } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'sp';

  const initialTotal = useSelector(getItemsTotal);
  const [total, setTotal] = useState(initialTotal);
  const totalRef = useRef<number | null>(initialTotal);
  const filters = useSelector(getFilters);
  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);

  const couponId = props.query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));

  const {
    isOpen: isDialogOpen,
    onToggle: onToggleDialogHook,
    onClose: onCloseFilterDialog,
  } = useDialogHandler(false);

  const searchMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchMinPrice = query.minPrice ? Number(query.minPrice) : null;
  const searchStarRating = query['starRatings[]'] || EMPTY_ARRAY;
  const searchReviewRating = query['reviewScores[]'] || EMPTY_ARRAY;

  const recentPriceFilter = filters.find(filter => filter.groupId === FILTER_GROUP_IDS.PRICE);
  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchMinPrice);
  const [starRating, setStarRating] = useState<Array<string>>(searchStarRating);
  const [reviewRating, setReviewRating] = useState<Array<string>>(searchReviewRating);
  const [sortKey, setSortKey] = useState(props.query?.sortKey || 'recommended');
  const { features, setFeatures, searchFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    query.features,
  );

  const filterNumber =
    searchFeatures.length +
    ~~!!searchMaxPrice +
    ~~isNotEmptyArray(searchStarRating) +
    ~~isNotEmptyArray(searchReviewRating);

  useEffect(() => {
    if (totalRef.current !== initialTotal) {
      totalRef.current = initialTotal;
      setTotal(initialTotal);
    }
  }, [initialTotal]);

  const onClickAwayDialog = useCallback(() => {
    onCloseFilterDialog();
    // reset filter state to the recent searched one
    setMinPrice(searchMinPrice);
    setMaxPrice(searchMaxPrice);
    setStarRating(searchStarRating);
    setReviewRating(searchReviewRating);
    setFeatures(searchFeatures);
    setTotal(initialTotal);
  }, [
    initialTotal,
    onCloseFilterDialog,
    searchFeatures,
    searchMaxPrice,
    searchMinPrice,
    searchReviewRating,
    searchStarRating,
    setFeatures,
  ]);

  /** To fetch a new total number after filtering */
  const updateTotalNumber = useCallback(
    async newCondition => {
      try {
        const response = await fetchProviderListItems(
          apiClient,
          getRequestBody(
            {
              params: { pageIdentifier: props.pathId },
              query: {
                ...props.query,
                minPrice,
                maxPrice,
                starRating,
                reviewRating,
                features,
                ...newCondition,
              },
            },
            couponDetail,
          ),
        );
        setTotal(response.total);
      } catch (error) {
        setTotal(initialTotal);
      }
    },
    [
      apiClient,
      couponDetail,
      features,
      initialTotal,
      maxPrice,
      minPrice,
      props.pathId,
      props.query,
      reviewRating,
      starRating,
    ],
  );

  const onSearchClick = useCallback(() => {
    onCloseFilterDialog();
    props.onSearch?.();
  }, [onCloseFilterDialog, props.onSearch]);

  const onDeferredSearch = useCallback(() => {
    // Note: only for providers filtering on TL/PC which not need confirm button action
    // can not use useEffect here since newest options will be changed when onSearch triggered
    setTimeout(() => onSearchClick(), 0);
  }, [onSearchClick]);

  const onCheckboxChanged = (filterGroupId: string, toggleOption: string) => {
    switch (filterGroupId) {
      case FILTER_GROUP_IDS.SORT: {
        setSortKey(toggleOption as SortKeysType);
        props.setSortKey?.(toggleOption as SortKeysType);
        break;
      }
      case FILTER_GROUP_IDS.RATING: {
        const nextRatings: Array<string> = toggleFilterOption(starRating, toggleOption);
        setStarRating(nextRatings);
        updateTotalNumber({ starRating: nextRatings });
        break;
      }
      case FILTER_GROUP_IDS.REVIEW_SCORE: {
        const nextReviewScores: Array<string> = toggleFilterOption(reviewRating, toggleOption);
        setReviewRating(nextReviewScores);
        updateTotalNumber({ reviewRating: nextReviewScores });
        break;
      }
      default: {
        const nextFeatures: Array<string> = toggleFilterOption(features, toggleOption);
        setFeatures(nextFeatures);
        updateTotalNumber({ features: nextFeatures });
        break;
      }
    }
  };

  // Price range handler
  const pendingSliderTimerRef = useRef<NodeJS.Timeout | number>(0);
  const isInitialState = useRef(true);
  const prevMinPrice = useRef<PriceRangeType>(searchMinPrice);
  const prevMaxPrice = useRef<PriceRangeType>(searchMaxPrice);

  /** to update total number on slider value changed */
  useEffect(() => {
    if (
      !isInitialState.current &&
      (minPrice !== prevMinPrice.current || maxPrice !== prevMaxPrice.current)
    ) {
      // to fetch with debouncing
      clearTimeout(pendingSliderTimerRef.current as number);
      pendingSliderTimerRef.current = setTimeout(() => {
        prevMinPrice.current = minPrice;
        prevMaxPrice.current = maxPrice;
        updateTotalNumber({ minPrice, maxPrice });
      }, DEBOUNCE_TIME);
    }

    isInitialState.current = false;
  }, [updateTotalNumber, minPrice, maxPrice]);

  const onPriceRangeChanged = (min?: number, max?: number) => {
    setMinPrice(min);
    setMaxPrice(max);
  };

  //For Reset: On clear bottom , reset all fields to initial value
  const onClearClick = () => {
    setMinPrice(null);
    setMaxPrice(null);
    setFeatures(EMPTY_ARRAY);
    setStarRating(EMPTY_ARRAY);
    setReviewRating(EMPTY_ARRAY);
    setSortKey('recommended');

    updateTotalNumber({
      minPrice: '',
      maxPrice: '',
      starRating: '',
      features: '',
      reviewRating: '',
    });
  };

  // selected filters
  const onRemoveStarRating = useCallback(() => {
    setStarRating(EMPTY_ARRAY);
    onDeferredSearch();
  }, [onDeferredSearch]);
  const onRemoveReviewRating = useCallback(() => {
    setReviewRating(EMPTY_ARRAY);
    onDeferredSearch();
  }, [onDeferredSearch]);
  const onRemovePriceRange = useCallback(() => {
    setMinPrice(null);
    setMaxPrice(null);
    onDeferredSearch();
  }, [onDeferredSearch]);
  const onRemoveFeatures = useCallback(
    ids => {
      setFeatures(prevFeatures => (prevFeatures || EMPTY_ARRAY).filter(id => !ids.includes(id)));
      onDeferredSearch();
    },
    [onDeferredSearch, setFeatures],
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClickAwayDialog();
      }
    }

    if (!isMobile && isDialogOpen) {
      // Bind the event listener
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      // Unbind the event listener on clean up
      if (!isMobile && isDialogOpen) {
        document.removeEventListener('click', handleClickOutside);
      }
    };
  }, [isDialogOpen, isMobile, onClickAwayDialog]);

  const appliedMin = minPrice ?? recentPriceFilter?.min;
  const appliedMax = maxPrice ?? recentPriceFilter?.max;

  // Dialog
  const onToggleFilterDialog = () => {
    if (isDialogOpen) {
      // if it's going to be closed without action, then reset to prev state
      onClickAwayDialog();
    } else {
      onToggleDialogHook();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={cx(props.className, styles.wrapper)}
      data-testid="filterContainer-wrapper"
    >
      {isNotEmptyArray(filters) && isMobile && (
        <button
          type="button"
          className={cx(styles.filterButton, props.filterButtonClassName)}
          onClick={onToggleFilterDialog}
          data-testid="filterContainer-toggleFilter-flatButton"
          aria-label="Filter"
        >
          <IconWithBadge
            badgeClassName={styles.filterBadge}
            icon={<Filter size={24} />}
            number={filterNumber}
          />
        </button>
      )}

      {isNotEmptyArray(filters) && !isMobile && (
        <div className={styles.filterAndSortWrapper}>
          <StyledButton
            className={styles.filterButtonWrapper}
            onClick={onToggleFilterDialog}
            title={<Translate id="Provider_List.Search.Sort_Filter.Filter_PC_TL" />}
            name="toggleFilter"
          />
          <ProviderFilterTags
            filters={filters}
            coupon={couponDetail}
            isGroupedFeatures={true}
            appliedMin={searchMinPrice}
            appliedMax={searchMaxPrice}
            onRemoveStarRating={onRemoveStarRating}
            onRemoveReviewRating={onRemoveReviewRating}
            onRemovePriceRange={onRemovePriceRange}
            onRemoveFeatures={onRemoveFeatures}
            onRemoveCoupon={onRemoveCoupon}
          />
        </div>
      )}

      <FilterAndSort
        // dialog
        onPage={onPage}
        isOpen={isDialogOpen}
        onClose={onToggleFilterDialog}
        onSearch={onSearchClick}
        onClearClick={onClearClick}
        // search
        filters={filters}
        minPrice={appliedMin}
        maxPrice={appliedMax}
        starRating={starRating}
        reviewRating={reviewRating}
        features={features}
        sortKey={sortKey}
        onCheckboxChanged={onCheckboxChanged}
        onPriceRangeChanged={onPriceRangeChanged}
        total={total}
      />

      {minPrice && <input type="hidden" name="minPrice" value={String(minPrice)} />}
      {maxPrice && <input type="hidden" name="maxPrice" value={String(maxPrice)} />}
      {isNotEmptyArray(starRating) &&
        starRating.map(rating => (
          <input key={rating} type="hidden" name="starRatings[]" value={rating} />
        ))}
      {isNotEmptyArray(reviewRating) &&
        reviewRating.map(score => (
          <input key={score} type="hidden" name="reviewScores[]" value={score} />
        ))}
      {isNotEmptyArray(features) && (
        <input type="hidden" name="features" value={features?.join(',')} />
      )}
      {/* SEO Params */}
      {sortKey && <input type="hidden" name={seoKeys.sortKey} value={sortKey} />}
    </div>
  );
}

export default FilterContainer;
